"use client";

import { useEffect, useCallback } from "react";
import { X, ExternalLink } from "lucide-react";
import { useTranslations } from "next-intl";

interface VideoDialogProps {
  videoId: string;
  title: string;
  channel: string;
  isOpen: boolean;
  onClose: () => void;
}

export function VideoDialog({
  videoId,
  title,
  channel,
  isOpen,
  onClose,
}: VideoDialogProps) {
  const t = useTranslations();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-dialog-title"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-4xl bg-card border border-border rounded-2xl overflow-hidden shadow-2xl animate-reveal-up">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex-1 min-w-0 pr-4">
            <h2
              id="video-dialog-title"
              className="font-display font-semibold text-foreground truncate"
            >
              {title}
            </h2>
            <p className="text-sm text-muted-foreground truncate">{channel}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-secondary transition-colors flex-shrink-0"
            aria-label={t("info_christian_close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedUrl}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="p-4 border-t border-border flex justify-end">
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            {t("info_christian_open_youtube")}
          </a>
        </div>
      </div>
    </div>
  );
}

interface VideoThumbnailProps {
  videoId: string;
  title: string;
  channel: string;
  onClick: () => void;
}

export function VideoThumbnail({
  videoId,
  title,
  channel,
  onClick,
}: VideoThumbnailProps) {
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

  return (
    <button
      onClick={onClick}
      className="group text-left w-full bg-secondary/50 rounded-xl overflow-hidden border border-border hover:border-primary/50 transition-all hover:shadow-lg"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg
              className="w-6 h-6 text-white ml-1"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h4 className="font-medium text-foreground text-sm line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h4>
        <p className="text-xs text-muted-foreground mt-1 truncate">{channel}</p>
      </div>
    </button>
  );
}
